import { useEffect, useRef } from "react";
import { load } from "@cashfreepayments/cashfree-js";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { payment, paymentVerify } from "../../store/Payment/PaymentAction";
import { paymentAction } from "../../store/Payment/PaymentReducer";
import Loading from "../../components/Loading";

const Payment = ({ visaAppliedDetail }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    paymentLoading,
    paymentDetail,
    paymentError,
    paymentSuccess,
    paymentVerifySuccess,
    paymentVerifyError,
    paymentVerifyLoading
  } = useSelector((state) => state.payment);

  const cashfreeRef = useRef(null);

  useEffect(() => {
    const loadSDK = async () => {
      cashfreeRef.current = await load({ mode: "production" });
    };
    loadSDK();
  }, []);

  useEffect(() => {
    const clearAllStates = () => {
      dispatch(paymentAction.clearPaymentVerifySuccess());
      dispatch(paymentAction.clearPaymentVerifyError());
      dispatch(paymentAction.clearPaymentDetail());
      dispatch(paymentAction.clearSuccess());
      dispatch(paymentAction.clearError());
      dispatch(paymentAction.clearLoading());
      dispatch(paymentAction.clearPaymentVerifyLoading());
    };
    if (paymentVerifySuccess) {
      navigate("/thankyou", { state: { paymentSuccess: true } });
      clearAllStates();
    }
  }, [paymentVerifySuccess, navigate, dispatch]);

  useEffect(() => {
    const clearAllStates = () => {
      dispatch(paymentAction.clearPaymentVerifySuccess());
      dispatch(paymentAction.clearPaymentVerifyError());
      dispatch(paymentAction.clearPaymentDetail());
      dispatch(paymentAction.clearSuccess());
      dispatch(paymentAction.clearError());
      dispatch(paymentAction.clearLoading());
      dispatch(paymentAction.clearPaymentVerifyLoading());
    };
    return () => {
      clearAllStates();
    };
  }, [dispatch]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    dispatch(payment({
      visaAppliedId: visaAppliedDetail._id,
      amount: visaAppliedDetail?.fairValue?.grandTotal,
      currency: visaAppliedDetail?.fairValue?.currency
    }));
  };

  useEffect(() => {
    const initiateCheckout = async () => {
      if (
        paymentSuccess &&
        paymentDetail?.payment_session_id &&
        paymentDetail?.order_id &&
        cashfreeRef.current // 👈 ensure it's loaded
      ) {
        try {
          let checkoutOptions = {
            paymentSessionId: paymentDetail.payment_session_id,
            redirectTarget: "_modal",
          };

          await cashfreeRef.current.checkout(checkoutOptions)
            .then(() => {
              dispatch(paymentVerify({ orderId: paymentDetail.order_id }));
            })
            .catch(() => {
              alert("Payment initialization failed. Please try again.");
            });

        } catch (error) {
          alert("Error during payment checkout.");
        }
      }
    };

    initiateCheckout();
  }, [paymentSuccess, paymentDetail, dispatch]);

  const currencySymbol = visaAppliedDetail?.fairValue?.currency === "INR" ? "₹" : "$";

  return (
    <> {paymentVerifyLoading ? <Loading> </Loading>: 
    <div className="payment-container">
      <div className="payment-header">
        <div className="payment-header__left">
          <svg className="payment-header__icon" aria-hidden="true">
            <use href="\sprite.svg#icon-sheet"></use>
          </svg>
          <div className="payment-header__date">Visa On {visaAppliedDetail?.deliveryDate}</div>
        </div>
        <span className="payment-header__processing">Processing</span>
      </div>

      <div className="order-summary-card">
        <div className="order-summary-header">
          <h2 className="order-summary-header__heading">Order Summary</h2>
        </div>

        <div className="order-details">
          <h3 className="order-details__visa-heading">{visaAppliedDetail?.visaId?.heading}</h3>

          <div className="order-details__travellers">
            <svg className="order-details__travellers-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <span className="order-details__travellers-text">{visaAppliedDetail?.numTravellers} Travelers Applying</span>
          </div>

          <div className="order-details__breakdown">
            <div className="order-details__row">
              <div className="order-details__label">Visa Fees</div>
              <div className="order-details__value">
                {currencySymbol}{visaAppliedDetail?.fairValue?.totalVisaAmount}
              </div>
            </div>

            <div className="order-details__row">
              <div className="order-details__charges">
                <div className="order-details__label">VisaFu Charges</div>
                {visaAppliedDetail?.fairValue?.discount > 0 && (
                  <span className="order-details__discount">{visaAppliedDetail?.fairValue?.discount}% off</span>
                )}
              </div>
              <div className="order-details__value">
                {currencySymbol}
                {visaAppliedDetail?.fairValue?.totalVisaFuCharge + visaAppliedDetail?.fairValue?.totalTax -
                  (visaAppliedDetail?.fairValue?.totalVisaFuCharge + visaAppliedDetail?.fairValue?.totalTax) *
                  visaAppliedDetail?.fairValue?.discount / 100}
              </div>
            </div>

            <div className="order-details__row order-details__row--total">
              <div className="order-details__label order-details__label--total">Total Amount</div>
              <div className="order-details__value order-details__value--total">
                {currencySymbol}{visaAppliedDetail?.fairValue?.grandTotal}
              </div>
            </div>

            <button className="order-details__pay-button" onClick={handleSubmit} disabled={paymentLoading}>
              {paymentLoading ? "Processing..." : `Pay ${currencySymbol}${visaAppliedDetail?.fairValue?.grandTotal}`}
              <svg className="order-details__pay-button-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="5 12 19 12 12 5 19 12 12 19"></polyline>
              </svg>
            </button>

            {(paymentError || paymentVerifyError) && (
              <div className="order-details__error">
                {paymentError || paymentVerifyError}
              </div>
            )}

            <div className="order-details__security">
              <div className="order-details__security-row">
                <svg className="order-details__security-icon order-details__security-icon--green" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>100% Secure Transaction</span>
              </div>
              <div className="order-details__security-row">
                <svg className="order-details__security-icon order-details__security-icon--blue" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <span>Your data is protected with 256-bit encryption</span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
}</>
  );
};

export default Payment;